import { get } from "svelte/store";
import config from "../config";
import layers from "../layers";
import {
  auth_logged_in,
  auth_client,
  auth_user,
  bookmarks,
  dev,
  layers_visible,
  leaflet_map,
  leaflet_layers,
  panel_selected,
  plots,
  plot_selected,
  plot_page,
  upload_status_code,
  upload_status_text,
} from "./store";
import util from "./util";

import * as tus from "tus-js-client";

function add_plot_layer(map, plot_obj) {
  const feature = {
    type: "Feature",
    geometry: plot_obj.geojson,
    properties: {
      plot_id: plot_obj.plot_id,
      plot_seq: plot_obj.plot_seq,
      mon_status: plot_obj.mon_status,
    },
  };
  function style_plot(feature) {
    switch (feature.properties.mon_status) {
      case null:
        return { color: "#aaa" };
      case "change":
        return { color: "#a44" };
      case "no-change":
        return { color: "#373" };
      default:
        return { color: "#aaa" };
    }
  }
  const layer = L.geoJSON(feature, { style: style_plot });
  layer.on("click", (e) => {
    const plot_seq = e.layer.feature.properties.plot_seq;
    panel_selected.set("plots");
    plot_selected.set(plot_seq);
  });
  plot_obj.layer = layer;
  layer.addTo(map);
}
async function create_user_settings(user) {
  for (const [bookm_seq, bookmark] of Object.entries(
    config.bookmarks.default
  )) {
    await update_bookmark(bookmark, "create");
  }
  set_layers(user);
}
function get_default_layers() {
  let layers_obj = {};
  for (const [layer_id, layer_config] of Object.entries(layers)) {
    if (layer_config.visible_default) {
      layers_obj[layer_id] = "on";
    }
  }
  return layers_obj;
}
async function get_token(client = "") {
  if (!client) {
    client = get(auth_client);
  }
  let token = "";
  if (client) {
    // const token = await client.getTokenSilently();
    const claims = await client.getIdTokenClaims();
    if (claims) {
      token = claims.__raw;
    }
  }
  return token;
}
function refresh_plot(plot_old, plot_new, map = "") {
  if (!map) {
    map = get(leaflet_map);
  }
  if (!map) {
    console.error("sync_plot: map is not yet initialised");
    return;
  }
  let items = get(plots);
  if (plot_old) {
    map.removeLayer(plot_old.layer);
  }
  if (plot_new) {
    if (plot_old && plot_old.plot_seq !== plot_new.plot_seq) {
      delete items[plot_old.plot_seq];
    }
    add_plot_layer(map, plot_new);
    items[plot_new.plot_seq] = plot_new;
  }
  plots.set(items);
}
function remove_plot_layers(map = "") {
  if (!map) {
    map = get(leaflet_map);
  }
  const plot_items = get(plots);
  for (const [plot_seq, plot_obj] of Object.entries(plot_items)) {
    map.removeLayer(plot_obj.layer);
  }
}
function set_default_state() {
  auth_logged_in.set(false);
  auth_user.set("");
  bookmarks.set(config.bookmarks.default);
  set_layers();
  remove_plot_layers();
  plots.set({});
  plot_page.set(0);
}
function set_layers(user) {
  let layer_conf = {};
  let map = get(leaflet_map);
  let map_layers = get(leaflet_layers);
  if (user && user.layers) {
    layer_conf = user.layers;
  } else {
    layer_conf = get_default_layers();
  }
  if (Object.keys(map_layers).length) {
    for (const layer_id of Object.keys(layers)) {
      if (!map_layers[layer_id]) {
        console.error(`Layer ${layer_id} does not exist`);
        continue;
      }
      const layer = map_layers[layer_id];
      if (layer_conf[layer_id] && layer_conf[layer_id] === "on") {
        if (!map.hasLayer(layer)) {
          map.addLayer(layer);
        }
      } else {
        if (map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
      }
    }
  }
  layers_visible.set(layer_conf);
}

async function sync_bookmarks(token, user) {
  let arr = await util.api_get(`bookmark_vw?user_id=eq.${user.user_id}`, token);
  let items = {};
  if (arr) {
    for (const bookm of arr) {
      const bookm_seq = String(bookm.bookm_seq).padStart(
        config.bookmarks.seq_length,
        "0"
      );
      items[bookm_seq] = bookm;
    }
  }
  bookmarks.set(items);
}
async function sync_plot(plot, user = "", token = "") {
  if (!user || !token) {
    user = get(auth_user);
    token = await get_token();
  }
  if (!user || !token) {
    set_default_state();
    return;
  }
  const plot_new = await util.api_get(
    `plot_monitor?plot_id=eq.${plot.plot_id}`,
    token,
    "one"
  );
  refresh_plot(plot, plot_new);
}
async function sync_plots(token, user) {
  const map = get(leaflet_map);
  if (!map) {
    console.error("sync_plots: map is not yet initialised");
    return;
  }
  let arr = await util.api_get(
    `plot_monitor?comp_id=eq.${user.comp_id}`,
    token
  );
  let items = {};
  remove_plot_layers(map);
  if (arr) {
    for (const plot of arr) {
      items[plot.plot_seq] = plot;
      add_plot_layer(map, plot);
    }
  }
  plots.set(items);
  plot_page.set(0);
}
async function sync_user_settings(token, user) {
  await sync_bookmarks(token, user);
  await sync_plots(token, user);
  set_layers(user);
}
async function update_auth(client) {
  let logged_in = false;
  if (client) {
    await client.checkSession();
    logged_in = await client.isAuthenticated();
  }
  if (!logged_in) {
    set_default_state();
    return;
  }
  const token = await get_token(client);
  const user_auth0 = await client.getUser();
  if (!token || !user_auth0) {
    set_default_state();
    return;
  }
  auth_logged_in.set(true);
  const id_auth = user_auth0.sub;
  let user = await util.api_get(
    `user_company?id_auth=eq.${id_auth}`,
    token,
    "one"
  );
  if (user) {
    // user is already present in the database
    let changes = {};
    if (user.email !== user_auth0.email) {
      // email changed on Auth0 side, updating it
      changes.email = user_auth0.email;
    }
    if (!user.layers) {
      // layers are not configured
      changes.layers = get_default_layers();
    }
    if (Object.keys(changes).length) {
      // PATCH already returns updated info, no need for additional GET
      user = await util.api_patch(
        `user_company?id_auth=eq.${id_auth}`,
        token,
        changes
      );
    }
    auth_user.set(user);
    await sync_user_settings(token, user);
    return;
  }

  // user not found, creating it
  let body = {};
  body.email = user_auth0.email;
  body.id_auth = id_auth;
  body.layers = get_default_layers();
  if (user_auth0.name !== user_auth0.email) {
    body.full_name = user_auth0.name;
  }
  // POST does not return info after triggers, so performing an additional GET
  await util.api_post("user_company", token, body);
  user = await util.api_get(`user_company?id_auth=eq.${id_auth}`, token, "one");
  auth_user.set(user);
  await create_user_settings(user);
}
async function update_bookmark(bookmark, change_type) {
  const user = get(auth_user);
  const token = await get_token();
  if (!user || !token) {
    set_default_state();
    return;
  }
  if (change_type === "create") {
    const body = {
      bookm_name: bookmark.bookm_name,
      bookm_seq: bookmark.bookm_seq,
      lat: bookmark.lat,
      long: bookmark.long,
      user_id: user.user_id,
      zoom: bookmark.zoom,
    };
    await util.api_post("bookmark_vw", token, body);
  } else if (change_type === "update") {
    const changes = {
      bookm_name: bookmark.bookm_name,
      bookm_seq: bookmark.bookm_seq,
    };
    await util.api_patch(
      `bookmark_vw?bookm_id=eq.${bookmark.bookm_id}`,
      token,
      changes
    );
  } else if (change_type === "delete") {
    const changes = {
      deleted: true,
    };
    await util.api_patch(
      `bookmark_vw?bookm_id=eq.${bookmark.bookm_id}`,
      token,
      changes
    );
  } else {
    console.error(`sync.update_bookmark: unknown change type ${change_type}`);
  }
  await sync_bookmarks(token, user);
}
async function update_plot(plot, change_type) {
  const user = get(auth_user);
  const token = await get_token();
  if (!user || !token) {
    set_default_state();
    return;
  }
  let plot_new;
  if (change_type === "create") {
    const body = {
      comp_id: user.comp_id,
      geojson: plot.geojson,
      plot_name: plot.plot_name,
    };
    plot_new = await util.api_post("plot_monitor", token, body);
    // have to make another get to get data after triggers
    plot_new = await util.api_get(
      `plot_monitor?plot_id=eq.${plot_new.plot_id}`,
      token,
      "one"
    );
  } else if (change_type === "update") {
    const changes = {
      cad_info: plot.cad_info,
      cad_nr: plot.cad_nr,
      ext_id: plot.ext_id,
      geojson: plot.geojson,
      plot_name: plot.plot_name,
      reg_id: plot.reg_id,
    };
    plot_new = await util.api_patch(
      `plot_monitor?plot_id=eq.${plot.plot_id}`,
      token,
      changes
    );
  } else if (change_type === "delete") {
    const changes = {
      deleted: true,
    };
    await util.api_patch(
      `plot_monitor?plot_id=eq.${plot.plot_id}`,
      token,
      changes
    );
  } else {
    console.error(`sync.update_plot: unknown change type ${change_type}`);
  }
  refresh_plot(plot, plot_new);
  if (plot_new) {
    return plot_new.plot_seq;
  }
}
async function update_user(changes) {
  let user = get(auth_user);
  const token = await get_token();
  if (!user || !token) {
    set_default_state();
    return;
  }
  user = await util.api_patch(
    `user_company?user_id=eq.${user.user_id}`,
    token,
    changes
  );
  auth_user.set(user);
}
async function upload_files(files, total_size, lang, init_uploaded = 0) {
  if (!files || !files.length) {
    console.error("Empty file array");
    return;
  }
  const file = files[0];
  const user = get(auth_user);
  const token = await get_token();
  if (!user || !token) {
    set_default_state();
    return;
  }
  const upload = new tus.Upload(file, {
    endpoint: config.url.upload_file,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    retryDelays: [0, 3000, 5000, 10000, 20000],
    metadata: {
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
      user_id: user.user_id,
      comp_id: user.comp_id,
    },
    onError: function (error) {
      if (dev) {
        console.log("Upload failure");
      }
      upload_status_code.set("upload-failure");
      upload_status_text.set(util.caption(lang, "upload", "failure_unknown"));
    },
    onProgress: function (uploaded) {
      const perc = (((init_uploaded + uploaded) / total_size) * 100).toFixed(0);
      upload_status_text.set(
        `${util.caption(lang, "upload", "progress")}: ${perc}%`
      );
    },
    onSuccess: function () {
      if (files.length > 1) {
        if (dev) {
          console.log("Upload success");
        }
        upload_files(
          files.slice(1, files.length),
          total_size,
          lang,
          init_uploaded + file.size
        );
      } else {
        upload_status_code.set("upload-success");
        upload_status_text.set(util.caption(lang, "upload", "success"));
      }
    },
  });
  if (dev) {
    console.log(`Uploading ${file.name}`);
  }
  // Check if there are any previous uploads to continue.
  const previousUploads = await upload.findPreviousUploads();
  if (previousUploads.length) {
    upload.resumeFromPreviousUpload(previousUploads[0]);
  }
  // Start the upload
  upload.start();
}
const sync = {
  sync_plot,
  update_auth,
  update_bookmark,
  update_plot,
  update_user,
  upload_files,
};

export default sync;
